import { useState } from "react";
import { Link, useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { api, formatErr } from "@/lib/api";
import AppHeader from "@/components/AppHeader";
import { ShieldCheck } from "lucide-react";

export default function Login() {
  const { user, login2faVerify, login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [useBackup, setUseBackup] = useState(false);
  const [stage, setStage] = useState("password");
  const [challenge, setChallenge] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  if (user) return <Navigate to={user.paid ? "/dashboard" : "/activate"} replace />;

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      const result = await login(email, password);
      if (result.needs_2fa) {
        setChallenge(result.challenge_token);
        setStage("2fa");
      } else {
        nav(result.paid ? "/dashboard" : "/activate");
      }
    } catch (e) {
      setError(formatErr(e.response?.data?.detail) || e.message);
    } finally { setLoading(false); }
  };

  const verify = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      const u = await login2faVerify(challenge, code);
      nav(u.paid ? "/dashboard" : "/activate");
    } catch (e) {
      setError(formatErr(e.response?.data?.detail) || e.message);
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-cream">
      <AppHeader />
      <div className="max-w-md mx-auto px-6 pt-20 pb-24">
        <div className="text-xs uppercase tracking-[0.2em] font-semibold text-sage mb-3">
          {stage === "password" ? "Sign in" : "Two-factor"}
        </div>
        <h1 className="font-serif text-4xl sm:text-5xl tracking-tight">
          {stage === "password" ? "Welcome back." : "Prove it's you."}
        </h1>
        <p className="text-moss mt-3">
          {stage === "password"
            ? "Your locks, bills, streaks and map — all waiting."
            : "Enter the 6-digit code from your authenticator app."}
        </p>

        {stage === "password" && (
          <form onSubmit={submit} className="mt-12 space-y-5" data-testid="login-form">
            <div>
              <label className="text-xs uppercase tracking-[0.2em] font-semibold text-sage">Email</label>
              <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                className="mt-2 w-full bg-cream border border-sand rounded-lg px-4 py-3 focus:ring-2 focus:ring-forest focus:border-transparent outline-none"
                data-testid="login-email" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.2em] font-semibold text-sage">Password</label>
              <input type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
                className="mt-2 w-full bg-cream border border-sand rounded-lg px-4 py-3 focus:ring-2 focus:ring-forest focus:border-transparent outline-none"
                data-testid="login-password" />
            </div>
            {error && <div className="text-sm text-clay bg-clay-light px-4 py-3 rounded-lg" data-testid="login-error">{error}</div>}
            <div className="text-right -mt-2">
              <Link to="/forgot-password" className="text-xs text-moss hover:text-forest" data-testid="login-forgot-link">Forgot your password?</Link>
            </div>
            <button type="submit" disabled={loading}
              className="w-full bg-forest text-white rounded-lg px-6 py-3.5 font-medium hover:bg-forest-deep disabled:opacity-60 transition-colors"
              data-testid="login-submit">
              {loading ? "Signing in…" : "Sign in"}
            </button>
          </form>
        )}

        {stage === "2fa" && (
          <form onSubmit={verify} className="mt-12" data-testid="login-2fa-form">
            <div className="flex items-center gap-3 bg-linen border border-sand rounded-lg px-4 py-3 mb-6">
              <ShieldCheck className="w-5 h-5 text-forest" strokeWidth={1.5} />
              <span className="text-sm text-moss">2FA is on for this account.</span>
            </div>
            <label className="text-xs uppercase tracking-[0.2em] font-semibold text-sage">
              {useBackup ? "Recovery code (XXXX-XXXX)" : "6-digit code"}
            </label>
            {!useBackup ? (
              <input type="text" inputMode="numeric" pattern="[0-9]*" maxLength={6} required
                value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                className="mt-2 w-full bg-cream border border-sand rounded-lg px-4 py-3 font-mono text-3xl tracking-[0.5em] text-center focus:ring-2 focus:ring-forest focus:border-transparent outline-none"
                placeholder="••••••" autoFocus data-testid="login-2fa-code" />
            ) : (
              <input type="text" required value={code} maxLength={9}
                onChange={(e) => setCode(e.target.value.toUpperCase().replace(/[^0-9A-F-]/g, ""))}
                className="mt-2 w-full bg-cream border border-sand rounded-lg px-4 py-3 font-mono text-2xl tracking-widest text-center focus:ring-2 focus:ring-forest focus:border-transparent outline-none"
                placeholder="XXXX-XXXX" autoFocus data-testid="login-backup-code" />
            )}
            <button type="button"
              onClick={() => { setUseBackup(!useBackup); setCode(""); setError(""); }}
              className="mt-3 text-xs text-forest hover:underline"
              data-testid="toggle-backup-mode">
              {useBackup ? "← Use authenticator code instead" : "Lost your phone? Use a recovery code →"}
            </button>
            {error && <div className="mt-4 text-sm text-clay bg-clay-light px-4 py-3 rounded-lg" data-testid="login-2fa-error">{error}</div>}
            <div className="mt-6 flex gap-3">
              <button type="button" onClick={() => { setStage("password"); setCode(""); setUseBackup(false); setError(""); }}
                className="text-sm text-moss hover:text-ink" data-testid="login-2fa-back">← Back</button>
              <button type="submit"
                disabled={loading || (useBackup ? code.length < 8 : code.length !== 6)}
                className="flex-1 bg-forest text-white rounded-lg px-6 py-3.5 font-medium hover:bg-forest-deep disabled:opacity-60 transition-colors"
                data-testid="login-2fa-submit">
                {loading ? "Verifying…" : "Verify & sign in"}
              </button>
            </div>
          </form>
        )}

        {stage === "password" && (
          <p className="mt-8 text-sm text-moss">
            No account yet?{" "}
            <Link to="/register" className="text-forest hover:text-forest-deep font-medium" data-testid="login-register-link">
              Open one — $45/month →
            </Link>
          </p>
        )}
      </div>
    </div>
  );
}
